import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { ASSETS, BUY_URL, CHART_URL } from "../../config";
import { StatusDot, Label } from "../primitives";
import { NeuralCanvas } from "../NeuralCanvas";
import { CopyCA } from "../CopyCA";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const OverlayReadout = ({ className, children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ delay, duration: 1 }}
    className={`pointer-events-none absolute border border-white/10 bg-ink-900/70 px-2.5 py-1.5 backdrop-blur-sm ${className}`}
  >
    <motion.div
      animate={{ y: [0, -6, 0] }}
      transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay }}
      className="font-mono text-[9px] uppercase tracking-[0.2em] text-pulse-soft/90"
    >
      {children}
    </motion.div>
  </motion.div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const artY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);

  return (
    <section ref={ref} className="grid-bg relative overflow-hidden pt-16" data-testid="hero">
      <NeuralCanvas className="pointer-events-none absolute inset-0 z-0 opacity-70" />
      <div className="relative z-10 mx-auto grid max-w-[1440px] gap-10 px-5 pb-16 pt-14 md:px-8 lg:grid-cols-12 lg:gap-6 lg:pb-24 lg:pt-24">
        <div className="relative z-10 flex flex-col justify-center lg:col-span-6 xl:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mb-7 flex items-center gap-3"
          >
            <StatusDot />
            <Label tone="green">Autonomous Finance Infrastructure</Label>
          </motion.div>

          <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-fog-100 sm:text-5xl lg:text-6xl" data-testid="hero-headline">
            <MaskedLine delay={0.25}>THE NEURAL LAYER</MaskedLine>
            <MaskedLine delay={0.4}>
              FOR <span className="text-transparent [-webkit-text-stroke:1px_#00C805]">AUTONOMOUS</span>
            </MaskedLine>
            <MaskedLine delay={0.55}>
              <span className="bg-gradient-to-r from-pulse-soft via-brass-bright to-brass bg-clip-text text-transparent">FINANCE</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-6 max-w-md text-sm leading-relaxed text-fog-400 sm:text-base"
          >
            SYNAPTIX connects intelligent agents, risk systems, and onchain capital into a coordinated
            autonomous financial layer.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-9 flex flex-wrap items-center gap-3"
          >
            <Link to="/terminal" data-testid="hero-enter-terminal"
              className="group flex items-center gap-2.5 bg-pulse px-6 py-3.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-ink-900 transition-all duration-200 hover:bg-pulse-soft hover:shadow-[0_0_32px_rgba(0,200,5,0.35)]">
              Enter Terminal
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
            <Link to="/agents" data-testid="hero-explore-agents"
              className="border border-white/15 px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-fog-100 transition-all duration-200 hover:border-brass/50 hover:text-brass-bright">
              Explore Agents
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 border-t border-white/[0.08] pt-5 font-mono text-[10px] uppercase tracking-[0.18em]"
            data-testid="hero-token-info"
          >
            <CopyCA testId="hero-copy-ca" />
            <a href={BUY_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-buy"
              className="flex items-center gap-2 text-fog-400 transition-colors hover:text-pulse-soft">
              <span className="text-fog-600">Buy</span> PONSFAMILY.COM
            </a>
            <a href={CHART_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-chart"
              className="flex items-center gap-2 text-fog-400 transition-colors hover:text-pulse-soft">
              <span className="text-fog-600">Chart</span> DEXTools
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.985 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10 lg:col-span-6 xl:col-span-7"
        >
          <div className="scanlines relative overflow-hidden border border-white/[0.1]" data-testid="hero-artwork">
            <motion.img
              src={ASSETS.art}
              alt="SYNAPTIX — autonomous finance artwork"
              style={{ y: artY }}
              className="h-full max-h-[560px] w-full scale-[1.06] object-cover"
              loading="eager"
              fetchpriority="high"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink-900/80 via-transparent to-ink-900/30" />
            <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-ink-900/70 to-transparent max-lg:hidden" />
            <OverlayReadout className="left-4 top-4" delay={1.2}>Robinhood Chain · Chain ID 4663</OverlayReadout>
            <OverlayReadout className="bottom-4 right-4" delay={1.45}>Network · Robinhood Chain</OverlayReadout>
            <OverlayReadout className="bottom-4 left-4 max-sm:hidden" delay={1.7}>Vault Engine · Standby</OverlayReadout>
          </div>
          <div className="mt-3 flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.22em] text-fog-600">
            <span className="flex items-center gap-2"><StatusDot /> Synaptix Core — System Online</span>
            <span>FIG. 01 — NEURAL LAYER</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
